import styled from "@emotion/styled";
import { useCallback, useMemo, type CSSProperties } from "react";

import {
  Form,
  FormikErrorMessages,
  FormikForm,
  IconButton,
  PropertiesPanelSection,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useEditingCss } from "../EditingCssPropertiesContext";
import { BorderInput } from "./BorderInput";
import { bordersSchema } from "./bordersSchema";
import { BorderStylePicker } from "./BorderStylePicker";
import { BorderTypePicker, type BorderType } from "./BorderTypePicker";
import {
  setIndividualBorderProperties,
  setSingleBorderProperties,
  type IndividualBorderCssProperties,
  type SingleBorderCssProperties,
} from "./utils";

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 10px;
  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: 16px;
`;

type FormValues = IndividualBorderCssProperties & SingleBorderCssProperties & Pick<CSSProperties, "borderStyle">;

export const BorderProperties = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onCssPropertiesChange } = useEditingCss();

  const hasSingleBorder = useMemo(() => !!cssProperties.borderWidth || !!cssProperties.borderColor, [cssProperties]);

  const hasBorderPerSide = useMemo(
    () =>
      !!cssProperties.borderTopWidth ||
      !!cssProperties.borderBottomWidth ||
      !!cssProperties.borderLeftWidth ||
      !!cssProperties.borderRightWidth,
    [cssProperties],
  );

  const hasNoBorder = useMemo(
    () => !cssProperties.borderStyle && !hasSingleBorder && !hasBorderPerSide,
    [cssProperties.borderStyle, hasSingleBorder, hasBorderPerSide],
  );

  const initialValues: FormValues = useMemo(
    () => ({
      borderStyle: cssProperties.borderStyle ?? "solid",
      borderColor: cssProperties.borderColor ?? "",
      borderWidth: cssProperties.borderWidth ?? "",
      borderTopColor: cssProperties.borderTopColor ?? "",
      borderTopWidth: cssProperties.borderTopWidth ?? "",
      borderBottomColor: cssProperties.borderBottomColor ?? "",
      borderBottomWidth: cssProperties.borderBottomWidth ?? "",
      borderLeftColor: cssProperties.borderLeftColor ?? "",
      borderLeftWidth: cssProperties.borderLeftWidth ?? "",
      borderRightColor: cssProperties.borderRightColor ?? "",
      borderRightWidth: cssProperties.borderRightWidth ?? "",
    }),
    [cssProperties],
  );

  const handleSubmit = useCallback((values: FormValues) => onCssPropertiesChange(values), [onCssPropertiesChange]);

  const handleAdd = useCallback(() => {
    onCssPropertiesChange?.({
      borderStyle: "solid",
      borderColor: "#000",
      borderWidth: "1px",
    });
  }, [onCssPropertiesChange]);

  const handleRemove = useCallback(() => {
    onCssPropertiesChange?.({
      borderStyle: undefined,
      ...setSingleBorderProperties(undefined, undefined),
      ...setIndividualBorderProperties(undefined, undefined),
    });
  }, [onCssPropertiesChange]);

  const borderType = useMemo((): BorderType => {
    if (hasSingleBorder && !hasBorderPerSide) return "single";
    if (hasBorderPerSide && !hasSingleBorder) return "individual";
    return "single"; // default
  }, [hasSingleBorder, hasBorderPerSide]);

  return (
    <PropertiesPanelSection
      title={formatMessage("sectionHeading")}
      empty={hasNoBorder}
      controls={
        hasNoBorder ? (
          <IconButton icon="plus" onClick={handleAdd} data-test="add-border-button" />
        ) : (
          <IconButton icon="minus" onClick={handleRemove} data-test="remove-border-button" />
        )
      }
      data-test="outline-properties-section"
    >
      {!hasNoBorder && (
        <>
          <FormikForm<FormValues>
            initialValues={initialValues}
            onSubmit={handleSubmit}
            submitOnBlur
            dataTest="outline-properties-form"
            validationSchema={bordersSchema}
            validateOnChange={false}
          >
            <Form>
              <Row>
                <BorderTypePicker
                  borderType={borderType}
                  cssProperties={cssProperties}
                  onCssPropertiesChange={onCssPropertiesChange}
                />
                <BorderStylePicker />
              </Row>

              {borderType === "single" ? (
                <BorderInput borderPosition="border" />
              ) : (
                <>
                  <BorderInput borderPosition="borderTop" />
                  <BorderInput borderPosition="borderBottom" />
                  <BorderInput borderPosition="borderLeft" />
                  <BorderInput borderPosition="borderRight" />
                </>
              )}

              <FormikErrorMessages />
            </Form>
          </FormikForm>
        </>
      )}
    </PropertiesPanelSection>
  );
};

const formatStrings = {
  sectionHeading: {
    en: "Outline",
  },
  singleBorder: {
    en: "Style outline as one",
  },
  individualBorders: {
    en: "Style outline per side",
  },
} satisfies FormatMessages;
